import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import SelectAccessLevel from "pages/Users/SelectAccessLevel";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ChangeAccessLevelRequest,
  UserModel,
} from "services/organizationPortalClient";
import translateError from "utils/translateError";

const useStyles = (theme: Theme) => ({
  title: {
    color: theme.palette.primary.dark,
  },
});

type ChangeAccessLevelDialogProps = {
  organizationUser?: UserModel;
  onClose: () => void;
};

export default function ChangeAccessLevelDialog({
  organizationUser,
  onClose,
}: ChangeAccessLevelDialogProps) {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { organizationUserService } = useGlobalContext();
  const { Form } = withTypes<ChangeAccessLevelRequest>();
  const init = {
    organizationUserId: organizationUser?.id,
    accessLevelId: organizationUser?.accessLevelId,
  } as ChangeAccessLevelRequest;
  const queryClient = useQueryClient();

  const submit = async (values: ChangeAccessLevelRequest) => {
    try {
      await organizationUserService.changeAccessLevel(
        ChangeAccessLevelRequest.fromJS(values)
      );
      queryClient.invalidateQueries(["organizationUserService.getUsers"]);
      onClose();
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const validate = (values: ChangeAccessLevelRequest) => {
    const errors = {} as any;
    if (!values.accessLevelId) {
      errors.accessLevelId = t({ id: "required" });
    }
    return errors;
  };

  return (
    <Dialog open={!!organizationUser} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.title}>
        <T id="selectNewAccessLevel" />
      </DialogTitle>
      <Form
        initialValues={init}
        onSubmit={submit}
        validate={validate}
        render={({ handleSubmit, submitting, pristine, submitError }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12}>
                    <T id="changeAccessLevelForSelectedUser" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={organizationUser?.firstName}
                      required
                      disabled
                      fullWidth
                      size="small"
                      label={t({ id: "firstName" })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={organizationUser?.lastName}
                      required
                      disabled
                      fullWidth
                      size="small"
                      label={t({ id: "lastName" })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={organizationUser?.email}
                      required
                      disabled
                      fullWidth
                      size="small"
                      label={t({ id: "email" })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="accessLevelId" required>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectAccessLevel
                            {...input}
                            required
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                    {submitError && (
                      <Alert sx={{ mt: 2, mb: -1 }} severity="error">
                        {submitError}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Grid container spacing={2} justifyContent="right">
                  <Grid item xs={12} sm={12} md={2}>
                    <Button onClick={onClose} fullWidth>
                      <T id="cancel" />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting || pristine}
                      fullWidth
                    >
                      {submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        <T id="confirm" />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
